import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { formatPeriod } from '../../../utils/formatPeriod';
import { isNil } from 'lodash';
import {
  FunnelTimeToConvertStatType,
  IFunnelTimeToConvertStatistics,
} from './utils';
import { UnreachableCaseError } from 'ts-essentials';

const Container = styled.div<{ $isCompactMode: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $isCompactMode }) =>
      $isCompactMode ? '2, minmax(112px, 240px)' : '4, minmax(112px, 240px)'}
  );
  grid-template-rows: repeat(auto-fill);
  grid-column-gap: 14px;
  grid-row-gap: 12px;
  justify-content: space-between;
  align-items: start;
`;

const Icon = styled.div`
  flex-shrink: 1; // avoid squashing the icon if the label is too wide
  display: flex;
  align-items: center;
`;

const Stat = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  display: inline-flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const Label = styled.div`
  word-wrap: break-word; // ensure that it doesn't grow over the allocated space
  min-width: 0;
`;

const Value = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const MedianIcon = () => (
  <svg
    viewBox="0 0 16 16"
    width="16"
    height="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line x1="0" y1="7" x2="16" y2="7" strokeWidth="2" stroke="currentColor" />
  </svg>
);

const AverageIcon = () => (
  <svg
    viewBox="0 0 16 16"
    width="16"
    height="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line
      x1="0"
      y1="7"
      x2="16"
      y2="7"
      strokeWidth="2"
      stroke="currentColor"
      strokeDasharray="4 2"
    />
  </svg>
);

const extendWithUiProps = (stats: IFunnelTimeToConvertStatistics) => {
  let additionalProps: {
    statName: string;
    icon: ReactElement;
    displayOrder: number;
  };
  const type = stats.type;
  switch (type) {
    case FunnelTimeToConvertStatType.MEDIAN:
      additionalProps = {
        statName: 'Median',
        icon: <MedianIcon />,
        displayOrder: 1,
      };
      break;
    case FunnelTimeToConvertStatType.AVERAGE:
      additionalProps = {
        statName: 'Average',
        icon: <AverageIcon />,
        displayOrder: 2,
      };
      break;
    default:
      throw new UnreachableCaseError(type);
  }

  return {
    ...stats,
    ...additionalProps,
  };
};

type ExtendedStats = ReturnType<typeof extendWithUiProps>;

// order stats but only within the same column. Since JS sort is stable, the different columns
// should remain in the order returned by the server
const statsOrderComparator = (a: ExtendedStats, b: ExtendedStats) => {
  if (a.key === b.key) {
    return a.displayOrder - b.displayOrder;
  } else {
    return 0;
  }
};

interface IProps {
  statistics: IFunnelTimeToConvertStatistics[];
  isCompactMode: boolean;
}

export const FunnelTimeToConvertStats = ({
  statistics,
  isCompactMode,
}: IProps) => {
  if (statistics.length === 0) {
    return null;
  }
  const extendedStats = statistics
    .map(extendWithUiProps)
    .sort(statsOrderComparator);
  return (
    <Container $isCompactMode={isCompactMode}>
      {extendedStats.map(
        ({ lineColor, icon, statName, value, sliceName, valueColor }, i) => {
          const formattedValue = formatPeriod(value) ?? 'n/a';
          return (
            <Stat $color={lineColor} key={i}>
              <Icon>{icon}</Icon>
              <Label>
                {!isNil(sliceName) && (
                  <>
                    {sliceName}
                    {' - '}
                  </>
                )}
                <Value $color={valueColor}>
                  {statName}
                  {': '}
                  {formattedValue}
                </Value>
              </Label>
            </Stat>
          );
        },
      )}
    </Container>
  );
};
